/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { buildImageUrl } from "../../theme1/utils/theme1Helpers";
import { formatNumber } from "utils/formatNumber";

export default function EditOrderModal({ order, products = [], theme, onCancel, onSave }) {
  const [draft, setDraft] = useState(null);
  const initialized = useRef(false);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [qty, setQty] = useState(1);
  const getOptionStock = (opt) => Number(opt?.stock ?? opt?.stockQty ?? 0);
  const resolveItemStock = (item) => {
    let currentStock = Number(item?.stockQty ?? 0);
    const prod = products.find(p => Number(p.id) === Number(item?.itemId));
    if (!prod) return Math.max(0, currentStock);
    const vari = prod.variants?.find(v => Number(v.variantId) === Number(item?.variantId));
    if (!vari) return Math.max(0, currentStock);
    const opt = (vari.options || vari.sizes || []).find(o => (o.label || o.size) === item?.size);
    if (opt) currentStock = getOptionStock(opt);
    return Math.max(0, currentStock);
  };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!order || initialized.current) return;
    initialized.current = true;

    const clean = JSON.parse(JSON.stringify(order));
    clean.details = (clean.details || []).map(d => ({
      ...d,
      itemId: d.itemId || d.ItemId,
      variantId: d.variantId || d.VariantId,
      itemName: d.itemName || d.ItemName,
      variantName: d.variantName || d.VariantName,
      colorName: d.colorName || d.ColorName,
      size: d.size || d.Size,
      quantity: Number(d.quantity || d.Quantity || 0),
      price: Number(d.price || d.Price || 0),
      discount: 0,
      imageUrl: d.imageUrl || d.ImageUrl,
      lineTotal: Number(d.lineTotal || d.LineTotal || (Number(d.quantity || d.Quantity || 0) * Number(d.price || d.Price || 0)) || 0)
    }));

    setDraft(clean);
  }, [order]);

  useEffect(() => {
    initialized.current = false;
  }, [order?.orderId]);

  // Handle adding product after customization (from theme1 ideas)
  const handleAddSelection = () => {
    if (!selectedVariant || !selectedSize) return;

    setDraft(prev => {
      const details = [...(prev.details || [])];
      const index = details.findIndex(d =>
        Number(d.variantId) === Number(selectedVariant.variantId) && d.size === (selectedSize.label || selectedSize.size)
      );

      const maxAvailable = getOptionStock(selectedSize);
      if (maxAvailable <= 0) {
        alert("هذا الخيار نفذ من المخزون");
        return prev;
      }

      if (index !== -1) {
        const item = details[index];
        const newQty = Number(item.quantity) + Number(qty);

        if (newQty > maxAvailable) {
          alert(`العفو، لا يمكنك إضافة أكثر من المتاح في المخزون (${maxAvailable} قطعة)`);
          return prev;
        }

        details[index] = {
          ...item,
          quantity: newQty,
          lineTotal: newQty * Number(item.price)
        };
      } else {
        if (Number(qty) > maxAvailable) {
          alert(`العفو، لا يمكنك إضافة أكثر من المتاح في المخزون (${maxAvailable} قطعة)`);
          return prev;
        }

        const finalPrice = Number(selectedVariant.finalPrice || selectedVariant.basePrice || 0);
        const basePrice = Number(selectedVariant.basePrice || 0);

        details.push({
          id: 0,
          orderId: prev.orderId,
          itemId: Number(selectedProduct.id),
          variantId: Number(selectedVariant.variantId),
          itemName: selectedProduct.name,
          variantName: selectedVariant.variantName || "",
          colorName: selectedVariant.color?.hex || selectedVariant.colorHex || "",
          size: selectedSize.label || selectedSize.size || "",
          quantity: Number(qty),
          price: finalPrice,
          imageUrl: selectedVariant.imageUrl || selectedProduct.mainImage,
          stockQty: maxAvailable,
          discount: basePrice - finalPrice,
          lineTotal: finalPrice * Number(qty)
        });
      }

      return { ...prev, details };
    });

    // Reset states
    setSelectedProduct(null);
    setSelectedVariant(null);
    setSelectedSize(null);
    setQty(1);
  };

  const updateQty = (i, delta) => {
    if (locked) return;
    setDraft(prev => {
      const details = [...prev.details];
      const item = details[i];
      if (!item) return prev;

      const q = Number(item.quantity) + delta;

      // Real stock check from products list (from theme1 ideas)
      const currentStock = resolveItemStock(item);

      if (delta > 0 && q > currentStock) {
        alert(`وصلت للحد الأقصى المتاح في المخزون لهذه القطعة (${currentStock} قطعة)`);
        return prev;
      }

      if (q <= 0) {
        // Confirm removal if needed, or just remove
        details.splice(i, 1);
      } else {
        details[i] = {
          ...item,
          quantity: q,
          stockQty: currentStock,
          lineTotal: q * Number(item.price)
        };
      }
      return { ...prev, details };
    });
  };

  const prepareOrder = () => {
    if (!draft.details) return null;
    const totalAmount = draft.details.reduce((s, i) => s + Number(i.price) * Number(i.quantity), 0);
    return {
      ...draft,
      totalAmount,
      netAmount: totalAmount - Number(draft.discountAmount || 0),
      tenantID: Number(draft.tenantId),
      items: draft.details.map(d => ({
        itemId: Number(d.itemId),
        variantId: Number(d.variantId),
        itemName: d.itemName,
        variantName: d.variantName,
        colorName: d.colorName,
        size: d.size,
        quantity: Number(d.quantity),
        price: Number(d.price),
        imageUrl: d.imageUrl,
        stockQty: Number(d.stockQty || 0),
        discount: Number(d.discount || 0),
        lineTotal: Number(d.quantity) * Number(d.price)
      }))
    };
  };

  if (!draft) return null;
  const locked = Number(draft.isPaid) === 1;

  return (
    <div className={`fixed inset-0 z-[27000] bg-gradient-to-br ${theme.bgGradient} flex items-center justify-center p-0 min-h-screen w-full overflow-y-auto custom-scrollbar`}>
      <div className="w-screen h-screen min-h-screen bg-transparent rounded-none border-none flex flex-col shadow-2xl relative">

        {selectedProduct &&
          createPortal(
            <div className={`fixed inset-0 z-[999999] bg-gradient-to-br ${theme.bgGradient} flex flex-col overflow-hidden`}>
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/5 bg-white/5 backdrop-blur-md z-20">
                <button
                  onClick={() => { setSelectedProduct(null); setSelectedVariant(null); setSelectedSize(null); }}
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-xl text-white hover:bg-white/10 transition-all"
                >
                  ×
                </button>
                <h3 className="text-xl font-bold text-center text-white">تخصيص المنتج</h3>
                <div className="w-10"></div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-xl mx-auto space-y-8 pb-32">
                  <div className="flex gap-4 mb-2 bg-white/5 p-4 rounded-2xl border border-white/5 items-center">
                    <img src={buildImageUrl(selectedVariant?.imageUrl || selectedProduct.mainImage)} className="w-20 h-20 object-contain rounded-xl bg-white p-1 shadow-sm" />
                    <div>
                      <div className="font-bold text-lg text-white">{selectedProduct.name}</div>
                      <div className="text-sm text-white/40">{selectedVariant?.variantName || "اختر اللون أولاً"}</div>
                      <div className="text-lg font-black mt-1" style={{ color: theme.primary }}>
                        {formatNumber(selectedVariant?.finalPrice || selectedVariant?.basePrice || selectedProduct.price)} IQD
                      </div>
                    </div>
                  </div>

                  {/* Color Selection */}
                  <div className="mb-6">
                    <label className="block text-[10px] font-bold text-white/40 mb-3 uppercase tracking-widest">1. اختر اللون</label>
                    <div className="flex flex-wrap gap-3">
                      {(() => {
                        const colorGroups = {};
                        (selectedProduct.variants || []).forEach(v => {
                          const hex = v.color?.hex || v.color?.hexCode || v.colorHex || "#333";
                          if (!colorGroups[hex]) {
                            colorGroups[hex] = v;
                          }
                        });

                        return Object.values(colorGroups).map(v => (
                          <button
                            key={v.variantId}
                            onClick={() => { setSelectedVariant(v); setSelectedSize(null); }}
                            className={`group relative p-1 rounded-full border-2 transition-all ${selectedVariant?.variantId === v.variantId ? 'scale-110' : 'border-transparent hover:border-white/20'}`}
                            style={{ borderColor: selectedVariant?.variantId === v.variantId ? theme.primary : 'transparent' }}
                          >
                            <div
                              className="w-8 h-8 rounded-full border border-white/10 shadow-inner"
                              style={{ background: v.color?.hex || v.color?.hexCode || v.colorHex }}
                            />
                            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-white text-black text-[10px] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10 pointer-events-none font-bold">
                              {v.variantName}
                            </div>
                          </button>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Size Selection */}
                  {selectedVariant && (
                    <div className="mb-6 animate-in slide-in-from-top-2 duration-300">
                      <label className="block text-[10px] font-bold text-white/40 mb-3 uppercase tracking-widest">2. اختر المقاس</label>
                      <div className="flex flex-wrap gap-2">
                        {(selectedVariant.options || selectedVariant.sizes || []).map(opt => {
                          const isOutOfStock = getOptionStock(opt) <= 0;
                          const isSelected = (selectedSize?.label || selectedSize?.size) === (opt.label || opt.size);
                          return (
                            <button
                              key={opt.label || opt.size}
                              disabled={isOutOfStock}
                              onClick={() => setSelectedSize(opt)}
                              className={`min-w-[50px] px-4 py-2 rounded-xl border font-bold text-sm transition-all ${isSelected
                                ? 'text-black'
                                : isOutOfStock ? 'bg-white/5 border-white/5 text-white/20 cursor-not-allowed' : 'bg-white/5 border-white/10 text-white hover:border-white/40'
                                }`}
                              style={{
                                backgroundColor: isSelected ? theme.primary : undefined,
                                borderColor: isSelected ? theme.primary : undefined
                              }}
                            >
                              {opt.label || opt.size}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Quantity Selection */}
                  {selectedSize && (
                    <div className="mb-8 animate-in slide-in-from-top-2 duration-300">
                      <label className="block text-[10px] font-bold text-white/40 mb-3 uppercase tracking-widest">3. الكمية</label>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center bg-white/5 border border-white/10 rounded-2xl p-1">
                          <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 flex items-center justify-center font-bold text-xl text-white hover:bg-white/10 rounded-xl transition-all">−</button>
                          <span className="min-w-[40px] flex items-center justify-center font-black text-lg text-white">{qty}</span>
                          <button
                            onClick={() => {
                              const stock = getOptionStock(selectedSize);
                              if (qty < stock) setQty(qty + 1);
                            }}
                            disabled={qty >= getOptionStock(selectedSize)}
                            className="w-10 h-10 flex items-center justify-center font-bold text-xl text-white hover:bg-white/10 rounded-xl transition-all disabled:opacity-20"
                          >
                            +
                          </button>
                        </div>
                        <div className="text-xs text-white/40">المتوفر: {getOptionStock(selectedSize)} قطعة</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 border-t border-white/5 bg-white/5 backdrop-blur-md sticky bottom-0">
                <div className="max-w-xl mx-auto">
                  <button
                    disabled={!selectedSize || getOptionStock(selectedSize) <= 0}
                    onClick={handleAddSelection}
                    className="w-full py-5 rounded-2xl font-black text-lg shadow-xl shadow-black/20 hover:scale-[1.01] active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    style={{ background: theme.primary, color: '#000' }}
                  >
                    تعديل الطلب
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        }

        {/* Header */}
        <div className="px-4 lg:px-8 py-5 lg:py-6 border-b border-white/5 flex justify-between items-center bg-white/5 backdrop-blur-md">
          <div>
            <h2 className="text-xl font-bold text-white">تعديل الطلب #{draft.orderNo}</h2>
            <p className="text-white/40 text-xs mt-1">يمكنك إضافة أو حذف المنتجات وتعديل الكميات</p>
          </div>
          <button onClick={onCancel} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors">
            <span className="text-white text-xl">×</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col lg:flex-row flex-1">

          {/* Left: Current Items */}
          <div className="flex-1 p-4 lg:p-6 space-y-4">
            <h3 className="text-white/60 text-sm font-medium mb-4">منتجات الطلب الحالية</h3>
            {(!draft.details || draft.details.length === 0) ? (
              <div className="h-40 flex flex-col items-center justify-center text-white/20 border-2 border-dashed border-white/5 rounded-3xl">
                <span>لا توجد منتجات في الطلب</span>
              </div>
            ) : (
              draft.details.map((item, i) => (
                (() => {
                  const itemStock = resolveItemStock(item);
                  const reachedMax = Number(item.quantity) >= itemStock;
                  return (
                    <div
                      key={i}
                      className="group flex flex-col sm:flex-row gap-4 bg-white/[0.03] hover:bg-white/[0.05] border border-white/5 rounded-2xl p-4 transition-all"
                    >
                      <div className="w-20 h-20 bg-white rounded-xl overflow-hidden flex-shrink-0">
                        <img
                          src={buildImageUrl(item.imageUrl)}
                          className="w-full h-full object-contain p-2"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-white font-bold truncate">{item.itemName}</h4>
                            <p className="text-white/40 text-xs mt-0.5">{item.variantName} {item.size ? `- ${item.size}` : ''}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-white font-bold">{formatNumber(item.price)} IQD</div>
                            <div className="text-[10px] text-white/30 truncate">الإجمالي: {formatNumber(item.lineTotal)}</div>
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-3">
                          <div className="flex items-center gap-2">
                            {item.colorName && (
                              <div className="flex items-center gap-1.5 px-2 py-1 bg-white/5 rounded-md border border-white/5">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ background: item.colorName }} />
                                <span className="text-[10px] text-white/60">لون</span>
                              </div>
                            )}
                            {item.size && (
                              <div className="px-2 py-1 bg-white/5 rounded-md border border-white/5">
                                <span className="text-[10px] text-white/60">{item.size}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center bg-black/40 rounded-lg p-1 border border-white/10 w-full sm:w-auto">
                            <button
                              disabled={locked}
                              onClick={() => updateQty(i, -1)}
                              className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-md transition-all active:scale-90 disabled:opacity-30"
                            >
                              −
                            </button>
                            <span className="w-8 text-center text-sm font-bold text-white">{item.quantity}</span>
                            <button
                              disabled={locked || reachedMax || itemStock <= 0}
                              onClick={() => updateQty(i, 1)}
                              className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-md transition-all active:scale-90 disabled:opacity-30"
                            >
                              +
                            </button>
                            {!locked && (
                              <button
                                onClick={() =>
                                  setDraft(d => ({
                                    ...d,
                                    details: d.details.filter((_, idx) => idx !== i)
                                  }))
                                }
                                className="ml-2 w-7 h-7 flex items-center justify-center text-red-500 hover:bg-red-500/10 rounded-md transition-all"
                                title="حذف"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()
              ))
            )}
          </div>

          <div className="w-full lg:w-96 border-t lg:border-t-0 lg:border-l border-white/5 bg-white/5 backdrop-blur-sm flex flex-col">
            <div className="p-6">
              <h4 className="text-white font-bold flex items-center gap-2">
                <span>إضافة منتجات</span>
                <span className="text-[10px] bg-white/10 text-white/60 px-2 py-0.5 rounded-full">{products.length}</span>
              </h4>
            </div>

            <div className="flex-1 px-4 lg:px-6 pb-6 space-y-2">
              {products.map(p => {
                // 1. Group variants by color
                const colorGroups = {};
                (p.variants || []).forEach(v => {
                  const hex = v.color?.hex || v.color?.hexCode || "#333";
                  if (!colorGroups[hex]) {
                    colorGroups[hex] = v;
                  }
                });
                const uniqueVariantsCount = Object.keys(colorGroups).length;

                // 2. Calculate final price (sync with theme1)
                const firstVariant = p.variants?.[0];
                let finalDisplayPrice = 0;
                if (firstVariant) {
                  const basePrice = Number(firstVariant.price || 0);
                  const preCalculatedDiscount = Number(firstVariant.discount || 0);
                  const preCalculatedFinal = Number(firstVariant.finalPrice || (basePrice - preCalculatedDiscount));
                  const dValue = Number(p.discountValue || 0);
                  const dType = Number(p.discountType || 0);
                  finalDisplayPrice = preCalculatedFinal;
                  if (preCalculatedDiscount === 0 && dValue > 0) {
                    let discountAmount = dType === 1 ? (basePrice * dValue) / 100 : dValue;
                    finalDisplayPrice = basePrice - discountAmount;
                  }
                } else {
                  finalDisplayPrice = Number(p.price || 0);
                }

                return (
                  <div
                    key={p.id}
                    onClick={() => {
                      setSelectedProduct(p);
                      if (p.variants?.length > 0) {
                        setSelectedVariant(p.variants[0]);
                      }
                    }}
                    className="group flex gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/5 cursor-pointer hover:border-white/20 hover:bg-white/[0.06] transition-all relative overflow-hidden active:scale-[0.98]"
                  >
                    <div className="w-14 h-14 bg-white rounded-lg flex-shrink-0 overflow-hidden shadow-lg border border-white/10">
                      <img src={buildImageUrl(p.mainImage)} className="w-full h-full object-contain p-1" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <b className="text-white text-xs block truncate group-hover:text-white">{p.name}</b>
                      <div className="text-[10px] text-white/40 truncate">{uniqueVariantsCount} ألوان متاحة</div>

                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-xs font-bold text-white">{formatNumber(finalDisplayPrice)} IQD</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-6 h-6 rounded-full bg-white text-black flex items-center justify-center text-sm font-bold">+</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 lg:p-8 border-t border-white/5 bg-white/5 backdrop-blur-xl flex flex-col md:flex-row gap-6 items-center">
          <div className="flex-1 text-right md:text-left w-full">
            <div className="text-white/40 text-xs">إجمالي الطلب</div>
            <div className="text-2xl font-black text-white">
              {formatNumber((draft.details || []).reduce((s, i) => s + Number(i.lineTotal), 0))} IQD
            </div>
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            <button onClick={onCancel} className="flex-1 md:w-32 py-4 rounded-2xl font-bold transition-all active:scale-95 border" style={{ color: theme.primary, borderColor: theme.primary, backgroundColor: "transparent" }}>
              إلغاء
            </button>
            {!locked && (
              <button
                onClick={() => onSave(prepareOrder())}
                className="flex-1 md:w-48 py-4 rounded-2xl font-black shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
                style={{ background: theme.primary, color: "#000" }}
              >
                <span>حفظ التعديلات</span>
                <span className="text-lg">✓</span>
              </button>
            )}
          </div>
        </div>
      </div >

      <style jsx="true">{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </div >
  );
}



